export const tidyEnd = (t: string) => t.trim().replace(/[.!\s]+$/, "");

/** Lower-case the first letter so a list item reads inside a sentence, but leave acronyms and brand-style words alone ("BPA-free", "iPhone", "USB-C"). */
export function midSentence(s: string): string {
  const t = tidyEnd(s);
  if (!t) return t;
  const first = t.split(/\s+/)[0];
  if (/[A-Z]/.test(first.slice(1))) return t; // BPA-free, USB-C, iPhone
  return t[0].toLowerCase() + t.slice(1);
}

export function joinList(items: string[], conj = "and"): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} ${conj} ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, ${conj} ${items[items.length - 1]}`;
}

export const articleFor = (word: string) => (/^(honest|hour|heir)/i.test(word.trim()) ? "an" : /^(uni(?!n)|use|user|one\b|eu)/i.test(word.trim()) ? "a" : /^[aeiou]/i.test(word.trim()) ? "an" : "a");

/** True for a plural category such as "Wireless Headphones" or "Running Shoes", which takes no "a"/"an". */
export function isPluralCategory(category: string): boolean {
  const last = category.trim().split(/\s+/).pop() ?? "";
  return /[a-z]s$/i.test(last) && !/(ss|us|is|as|ics)$/i.test(last);
}

export function describe(p: { name: string; category: string; features: string[]; benefits: string[]; audience: string }) {
  const name = p.name.trim(), category = p.category.trim(), audience = p.audience.trim();
  const plural = isPluralCategory(category);
  const art = plural ? "" : `${articleFor(category)} `;
  const feats = p.features.map(midSentence).filter(Boolean);
  const bens = p.benefits.map(tidyEnd).filter(Boolean);
  const featText = joinList(feats);
  const benefitSentences = bens.length ? bens.map((b) => b + ".").join(" ") : "";
  const firstBenefit = bens[0] ?? "";
  const forWhom = audience ? ` for ${audience}` : "";

  const featureBenefit = [
    `Meet the ${name}, ${art}${category}${feats.length ? ` with ${featText}` : ""}${audience ? `, made for ${audience}` : ""}.`,
    benefitSentences,
  ].filter(Boolean).join(" ");

  // Opens on the problem, then lists the benefits; the features appear once, not again in the opening line.
  const problemSolution = [
    plural ? `Not all ${category} are built to last.` : `Not every ${category} is built to last.`,
    `The ${name} is${forWhom ? ` designed${forWhom}` : " built differently"}${feats.length ? `, with ${featText}` : ""}.`,
    benefitSentences ? `What that means: ${benefitSentences}` : "",
  ].filter(Boolean).join(" ");

  const shortPunchy = [
    `${name}: ${art}${category}${forWhom}.`,
    feats.length ? `${feats.map((f) => f[0].toUpperCase() + f.slice(1)).join(". ")}.` : "",
    firstBenefit ? `${firstBenefit}.` : "",
  ].filter(Boolean).join(" ");

  return [
    { label: "Feature → Benefit", text: featureBenefit },
    { label: "Problem → Solution", text: problemSolution },
    { label: "Short & Punchy", text: shortPunchy },
  ];
}
